<script lang="ts" setup>
import PagedModal from "@uicore/PagedModal.vue";
import SpinningLoader from "@uicore/SpinningLoader.vue";
import { computed, nextTick, onBeforeUnmount, onMounted, Ref, ref } from "vue";
import { useRouter } from "vue-router";
import { EnumUiEvents } from "@shared/enums";
import { dispatchEvent, handleEvent } from "@shared/Events";
import { TUiAuthData } from "@shared/types";
import useNotification from "./useNotification";
import { currentAccount } from "@shared/Global";
import { mobileCanRetrieve, mobileSetUserId } from "@/mobile/postMessage";
const { locale } = useI18n();
import { isNativeMobileApp } from "@/mobile/isOnIOS";
import isMobile, { getMobileDevice } from "@shared/IsMobile";
import { useI18n } from "vue-i18n";
import { ignoreDownloadStep } from "./Signup/SignupSteps";
// import { retrieveAvailableDemoAccount } from "@/mobile/demoAccounts";

defineProps({
  visible: {
    type: Boolean,
  },
});

const email = ref(localStorage.getItem("email") || "");
const passwordInput: Ref<HTMLInputElement | null> = ref(null);
const password = ref(localStorage.getItem("password") || "");
const rememberPassword = ref(!!localStorage.getItem("email") || false);
const isLoading = ref(false);
const loadingText = ref("Loading...");
// const loginError = ref("");
// const queryReferral = useRoute().query.r;
const router = useRouter();
const currentStep = ref(0);
const hasError = computed(() => {
  switch (currentStep.value) {
    case 0:
      return (
        !!email.value &&
        !/^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,})+$/.test(email.value)
      );
    case 1:
      return !isPasswordValid.value;
    default:
      return false;
  }
});

const isMobileBrowser =
  isMobile() && !isNativeMobileApp() && !ignoreDownloadStep.value;

const isAppleDevice = ref(getMobileDevice().apple.device);
const badgeUrl = computed(
  () =>
    `/assets/overlay/marketing/${
      isAppleDevice.value ? "app-store" : "google-play"
    }-badge-${locale.value}.svg`,
);
const storeURL = "https://app.adjust.com/12rsjchy";

const isStepComplete = computed(() => {
  if (hasError.value) {
    return false;
  }
  switch (currentStep.value) {
    case 0:
      return (
        email.value &&
        /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,})+$/.test(email.value)
      );
    case 1:
      return isPasswordValid.value;
    default:
      return false;
  }
});
const ctaButtonText = computed(() => {
  if (currentStep.value == 1) {
    return "Login";
  }
  if (updateOverlay.value) {
    return "Update";
  }
  return "Next";
});
const showError = ref(false);
const errorMessage = computed(() => {
  if (currentStep.value == 0) {
    return "Email is incorrect";
  } else {
    return "Password must be at least 8 characters and consist of at least a capital letter, a small letter and a number.";
  }
});

const ctaBtnDisabled = computed(() => {
  if (updateOverlay.value) {
    return false;
  }
  return !isStepComplete.value;
});

async function handleCtaClick() {
  if (updateOverlay.value) {
    if (getMobileDevice().apple) {
      window.open(
        "https://apps.apple.com/us/app/bit-hotel-fun-social-games/id6446282164?app=bithotel",
      );
    } else if (getMobileDevice().android) {
      window.open(
        "https://play.google.com/store/apps/details?id=play.bithotel.io",
      );
    }
    // window.open("https://bithotel.io/app");
    return;
  }
  if (!isStepComplete.value) {
    showError.value = true;
    return;
  }
  showError.value = false;
  // errorMessage.value = "";
  if (ctaButtonText.value === "Login") {
    loadingText.value = "Logging in...";
    isLoading.value = true;
    if (rememberPassword.value) {
      localStorage.setItem("email", email.value);
      localStorage.setItem("password", password.value);
    } else {
      localStorage.removeItem("email");
      localStorage.removeItem("password");
    }
    dispatchEvent(EnumUiEvents.UiLogin, {
      email: email.value,
      password: password.value,
    } as TUiAuthData);
    return;
  }
  currentStep.value++;
  if (currentStep.value === 1) {
    await nextTick();
    passwordInput.value && passwordInput.value.focus();
  }
}

// async function handleDemoLogin() {
//   const ret = await retrieveAvailableDemoAccount();
//   if (!ret) {
//     dispatchEvent(EnumUiEvents.UiLoginFailed, {
//       message:
//         "All demo accounts are currently unavailable. Please try again later!",
//     });
//     return;
//   }
//   loadingText.value = "Logging in...";
//   isLoading.value = true;
//   if (rememberPassword.value) {
//     localStorage.setItem("email", ret.email);
//     localStorage.setItem("password", ret.password);
//   } else {
//     localStorage.removeItem("email");
//     localStorage.removeItem("password");
//   }
//   dispatchEvent(EnumUiEvents.UiLogin, {
//     email: ret?.email,
//     password: ret?.password,
//   } as TUiAuthData);
// }

const updateOverlay = ref<boolean>(false);

onMounted(async () => {
  if (isNativeMobileApp()) {
    // !! For testing mobileTrackAdjust(AdjustEvent.WATCH_AD, {});
    if (!(await mobileCanRetrieve())) {
      updateOverlay.value = true;
    }
  }
});

const loginError = ref("");
handleEvent(
  EnumUiEvents.UiLoginFailed,
  (data: { message: string }) => {
    setTimeout(() => {
      loadingText.value = "Let's try that again...";
      setTimeout(() => {
        isLoading.value = false;
        currentStep.value = 0;
        password.value = "";
        loginError.value = data?.message;
        showError.value = true;
      }, 700);
    }, 300);
  },
  onBeforeUnmount,
);
handleEvent(
  EnumUiEvents.UiLoginSuccess,
  () => {
    mobileSetUserId(currentAccount.user.id);
    setTimeout(() => {
      loadingText.value = "...success.";
      setTimeout(() => {
        loadingText.value = "Entering Bit Hotel...";
      }, 700);
    }, 300);
  },
  onBeforeUnmount,
);
const isPasswordValid = computed(() => {
  return password.value.length > 0;
});

// const errorMessage = computed(()=>{
//   if(currentStep.value==0){
//     return "Email is "
//   }
// })

function toggleCheckbox(e: any) {
  rememberPassword.value = e.target?.checked;
}

// For showing a message in case the server is under construction
const { notification: loginNotification, disableNavigation } =
  useNotification("login");

const ignoreNotifications =
  import.meta.env.VITE_IGNORE_NOTIFICATION_DISABLE === "true";
</script>
<template>
  <paged-modal
    :pages="
      disableNavigation || updateOverlay
        ? [{ name: 'default' }]
        : [{ name: 'login' }, { name: 'signup' }]
    "
    :show-inactive-names="true"
    :visible="true"
    active-page="login"
    class="modal-auth unset-width"
    :class="{ 'no-pointer': disableNavigation }"
    @update:active-page="
      if (!disableNavigation)
        router.push({
          name: $event,
          query: { campaign: router.currentRoute.value.query['campaign'] },
        });
    "
  >
    <template #header-inner>
      <div class="modal-title flex center full-width">
        <img class="logo" src="/assets/overlay/titles/bithotel.png" />
        <!-- <img class="logo blur" src="@overlay/assets/titles/bithotel.png" /> -->
      </div>
    </template>

    <div
      class="modal-content flex vertical gap--s padding-t--ml padding-b--sm padding-h--m"
    >
      <span
        v-if="loginNotification && loginNotification.disable"
        class="info font font--s normal depixel padding-h--xs padding-v--xxsm border-radius--xxs color-bg--red-75 color-text--red-500"
      >
        {{ loginNotification.message }}
      </span>
      <span
        v-if="showError"
        class="info font font--s normal depixel padding-h--xs padding-v--xxsm border-radius--xxs color-bg--red-75 color-text--red-500"
      >
        {{ loginError || errorMessage }}
      </span>
      <form
        v-if="
          !loginNotification ||
          ignoreNotifications ||
          (loginNotification && loginNotification.disable !== true)
        "
        class="login flex column"
        @submit.prevent="handleCtaClick"
      >
        <template v-if="currentStep === 0 && !isMobileBrowser">
          <label class="font normal depixel color--black" for="email">{{
            updateOverlay
              ? "Please update the app in order to sign in"
              : "Email"
          }}</label>
          <input
            v-if="!updateOverlay"
            v-model="email"
            :class="{ error: hasError && showError }"
            autofocus
            class="font depixel normal color--black"
            name="email"
            id="email"
            type="email"
          />
        </template>
        <template v-else-if="currentStep === 1 && !isMobileBrowser">
          <label class="font normal depixel color--black" for="password">
            Password:
          </label>
          <input
            ref="passwordInput"
            v-model="password"
            :class="{ error: hasError && showError }"
            class="font depixel normal color--black"
            name="password"
            id="password"
            type="password"
          />
          <div
            class="back font normal depixel color--black font--xxs"
            @click="currentStep = 0"
          >
            &lt; Back
          </div>
          <div class="wrapper">
            <input
              id="remember-password"
              :checked="rememberPassword"
              name="remember-password"
              type="checkbox"
              value="true"
              @change="toggleCheckbox"
            />
            <label class="font depixel normal" for="remember-password">
              Remember password
            </label>
          </div>
        </template>
        <div v-if="isMobileBrowser">
          <span v-depixel-normal class="font--s bottom-md">
            {{
              $t("pages.signup.download.download-store", {
                store: isAppleDevice ? "App" : "Play",
              })
            }}
          </span>
          <a class="width--full" :href="storeURL" target="_self">
            <img
              class="width--full"
              :src="badgeUrl"
              :alt="isAppleDevice ? 'App' : 'Play' + ' store badge'"
            />
          </a>
        </div>
        <button
          :disabled="ctaBtnDisabled"
          class="bluegreen highlighted--l"
          id="login"
          type="button"
          @click="handleCtaClick"
          v-if="!isMobileBrowser"
        >
          <span class="font depixel bold outlined shadow color--white">{{
            ctaButtonText
          }}</span>
        </button>
        <spinning-loader v-if="isLoading" :text="loadingText" />
      </form>
      <!-- <div
        v-else
        class="modal-content full-width font font--ssm normal depixel pointer text-center padding-h--s spacer-t--s color-text--blue-200:hover--blue-300"
      >
        <p>Bit Hotel is not playable on mobile phones.</p>
      </div> -->
      <div
        v-if="!disableNavigation && !isMobileBrowser && !updateOverlay"
        class="full-width font font--ssm normal depixel pointer text-center padding-h--s spacer-t--s color-text--blue-200:hover--blue-300"
        @click="
          router.push({
            name: 'forgot-password',
            query: { campaign: router.currentRoute.value.query['campaign'] },
          })
        "
      >
        Forgot Password?
      </div>
      <!-- <div
        v-if="!disableNavigation && !isMobileBrowser"
        class="full-width font font--ssm normal depixel pointer text-center padding-h--s spacer-t--s color-text--blue-200:hover--blue-300"
        @click="handleDemoLogin
        "
      >
      Login as guest
      </div> -->
    </div>
  </paged-modal>
</template>

<style lang="scss" scoped src="./style.scss" />
